import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr, inr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, MessageCircle, BarChart3, UserCheck, BedDouble, Binoculars, ClipboardList, PlusCircle, CreditCard, Landmark, Tag, Users, Star as StarIcon, BookOpen, Bell, SlidersHorizontal, ShieldCheck, Menu, X } from "lucide-react";
import { HotelManager, PackageManager } from "@/pages/AdminStays";
import { ModifyDialog, CancelDialog } from "@/components/ManageBooking";
import { RequestList, SessionsEditor } from "@/pages/AdminRequests";
import FleetManager, { FleetList } from "@/pages/AdminFleet";
import DispatchSheet from "@/pages/AdminDispatch";
import JeepBoard from "@/pages/AdminJeepBoard";
import SupplementManager from "@/pages/AdminSupplements";
import BlogManager from "@/pages/AdminBlog";
import ContentManager from "@/pages/AdminContent";
import AlertSettings from "@/pages/AdminAlerts";
import ReviewsManager from "@/pages/AdminReviews";
import { Overview, Payments } from "@/pages/AdminOverview";
import { Promos, Customers, AuditLog } from "@/pages/AdminPromos";
import { CreateBooking } from "@/pages/AdminCreateBooking";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format, differenceInCalendarDays } from "date-fns";

const ymd = (d) => format(d, "yyyy-MM-dd");
const toDate = (s) => (s ? new Date(`${s}T00:00`) : undefined);
const fmtDay = (s) => format(toDate(s), "dd MMM yyyy");
const dayCount = (a, b) => differenceInCalendarDays(toDate(b || a), toDate(a)) + 1;

const NAV = [
  ["overview", "Overview & revenue", BarChart3],
  ["partners", "Partner approvals", UserCheck],
  ["hotels", "Hotels, rooms & rates", BedDouble, [["stays", "Hotels & rooms"], ["packages", "Packages"]]],
  ["inventory", "Safari live inventory", Binoculars, [["safaris", "Safaris"], ["slots", "Slots"], ["board", "Jeep board"], ["supplements", "Supplements"], ["fleet", "Fleet & drivers"], ["dispatch", "Dispatch"], ["locations", "Pickup locations"]]],
  ["bookings", "All bookings", ClipboardList, [["bookings", "Bookings"], ["requests", "On request"]]],
  ["create", "Create booking", PlusCircle],
  ["payments", "Payments & refunds", CreditCard],
  ["commissions", "Commissions & settlements", Landmark],
  ["promos", "Promo codes", Tag],
  ["customers", "Customers", Users],
  ["reviews", "Reviews", StarIcon],
  ["content", "Content & support", BookOpen, [["content", "Site content"], ["blog", "Blog"]]],
  ["notifications", "Notifications", Bell],
  ["staff", "Staff & settings", SlidersHorizontal],
  ["audit", "Audit log", ShieldCheck],
];

function NextRound({ title, text }) {
  return <div className="bg-white ring-1 ring-black/5 rounded-2xl p-8 max-w-2xl" data-testid="next-round"><div className="font-serif text-xl text-[#1E3A2B] mb-2">{title}</div><p className="text-sm text-[#5C6B63]">{text}</p></div>;
}

export default function AdminDashboard() {
  const [section, setSection] = useState("overview");
  const [menu, setMenu] = useState(false);
  const [tab, setTab] = useState("stays");
  const [dispatchDate, setDispatchDate] = useState(null);
  const { user, logout, loading } = useAuth();
  if (loading) return <div className="jt-container py-20">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  const go = (key) => { const item = NAV.find((n) => n[0] === key); setSection(key); if (item[3]) setTab(item[3][0][0]); };
  const cur = NAV.find((n) => n[0] === section);
  const pane = {
    stays: <HotelManager />, packages: <PackageManager />, safaris: <SafariManager />, slots: <SlotManager />,
    board: <JeepBoard onDispatch={(d) => { setDispatchDate(d); setSection("inventory"); setTab("dispatch"); }}/>,
    supplements: <SupplementManager />, fleet: <FleetManager />, dispatch: <DispatchSheet key={dispatchDate || "default"} initialDate={dispatchDate}/>,
    locations: <div className="mt-6"><p className="text-sm text-[#5C6B63] mb-4 max-w-3xl">Guests choose these at checkout. Pickup and drop prices are added to the booking total (per booking, or per jeep for multi-jeep groups). Pickup time = departure time minus the minutes set here.</p><FleetList kind="locations"/></div>,
    bookings: <BookingList />, requests: <RequestList />, content: <ContentManager />, blog: <BlogManager />,
  };
  const single = {
    overview: <Overview />, create: <CreateBooking />, payments: <Payments />, promos: <Promos />,
    customers: <Customers />, reviews: <ReviewsManager />, notifications: <AlertSettings />, audit: <AuditLog />,
    partners: <NextRound title="Partner approvals: coming in the next update" text="You'll add hotel partners yourself, with their login, commission rate and the hotels they manage."/>,
    commissions: <NextRound title="Commissions & settlements: coming in the next update" text="Monthly statements per hotel partner: paid bookings, your commission, amount due, and a Mark settled button."/>,
    staff: <NextRound title="Staff & settings: coming in the next update" text="Staff logins with roles: Owner (everything), Manager (bookings, inventory, content) and Staff (bookings and dispatch only)."/>,
  };

  return (
    <div className="min-h-screen md:flex bg-[#FBF8F4]">
      <div className="md:hidden sticky top-0 z-30 flex items-center justify-between bg-[#16241C] px-4 py-3 print:hidden">
        <img src="/logo-light.png" alt="JawaiTrails" className="h-7"/>
        <button onClick={() => setMenu(!menu)} className="text-white p-1" aria-label="Menu" data-testid="admin-menu-toggle">{menu ? <X size={22}/> : <Menu size={22}/>}</button>
      </div>
      <aside className={`${menu ? "flex fixed inset-0 top-[52px] z-30 w-full" : "hidden"} md:flex md:sticky md:top-0 md:w-64 md:h-screen shrink-0 bg-[#16241C] text-[#C9D2CC] flex-col print:hidden`} data-testid="admin-sidebar">
        <div className="hidden md:block px-6 pt-6 pb-4"><img src="/logo-light.png" alt="JawaiTrails" className="h-9"/><div className="text-[10px] tracking-[0.3em] mt-5 text-[#8EA094]">JAWAITRAILS ADMIN</div></div>
        <nav className="flex-1 overflow-y-auto px-3 py-3 md:py-0 space-y-0.5">
          {NAV.map(([key, label, Icon]) => (
            <button key={key} onClick={() => { go(key); setMenu(false); }} data-testid={`nav-${key}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-left transition-colors border-l-2 ${section === key ? "bg-[#22362A] text-white border-[#C86D51]" : "border-transparent hover:bg-white/5 hover:text-white"}`}>
              <Icon size={17} className="shrink-0"/>{label}
            </button>
          ))}
        </nav>
        <div className="border-t border-white/10 px-6 py-4 text-xs"><div className="truncate">{user.email}</div>
          <button onClick={logout} className="mt-2 inline-flex items-center gap-2 text-white" data-testid="admin-logout"><LogOut size={14}/>Sign out</button></div>
      </aside>
      <main className="flex-1 min-w-0 px-4 md:px-10 py-6 md:py-8 overflow-x-auto">
        <h1 className="font-serif text-3xl text-[#1E3A2B] mb-6 print:hidden" data-testid="admin-section-title">{cur[1]}</h1>
        {cur[3] ? (
          <>
            <div className="flex flex-wrap gap-2 print:hidden">{cur[3].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`} className={`px-4 py-1.5 rounded-full text-sm ${tab === k ? "bg-[#1E3A2B] text-white" : "bg-white ring-1 ring-black/5"}`}>{l}</button>
            ))}</div>
            {pane[tab]}
          </>
        ) : <div>{single[section]}</div>}
      </main>
    </div>
  );
}

function StatsStrip() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      <Stat label="Total bookings" value={s.total_bookings} testid="stat-total"/>
      <Stat label="Confirmed" value={s.confirmed_bookings} testid="stat-confirmed"/>
      <Stat label="Pending" value={s.pending_bookings} testid="stat-pending"/>
      <Stat label="Revenue" value={inr(s.total_revenue)} testid="stat-revenue"/>
      <Stat label="Open requests" value={s.open_requests} testid="stat-open-requests"/>
    </div>
  );
}
function Stat({ label, value, testid }) {
  return (
    <div className="bg-white rounded-xl p-5 ring-1 ring-black/5">
      <div className="text-xs text-[#5C6B63]">{label}</div>
      <div className="font-serif text-2xl text-[#1E3A2B] mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}

const BLANK_SAFARI = {
  name: "", category: "private", short_description: "", description: "",
  duration_hours: 3, base_price: 5000, pricing_mode: "per_vehicle",
  vehicle_capacity: 6, inclusions: [], exclusions: [], pickup_info: "",
  cancellation_policy: "", faqs: [], image_url: "", gallery: [], is_active: true,
  shared_pool: true, booking_mode: "instant",
  sessions: [{ label: "Morning", time: "06:30" }, { label: "Evening", time: "16:00" }],
};

function SafariManager() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK_SAFARI);

  const load = () => api.get("/admin/safaris").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(BLANK_SAFARI); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...BLANK_SAFARI, ...s }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, pricing_mode: "per_vehicle",
        duration_hours: Number(form.duration_hours),
        base_price: Number(form.base_price),
        vehicle_capacity: Number(form.vehicle_capacity),
        inclusions: typeof form.inclusions === "string" ? form.inclusions.split("\n").filter(Boolean) : form.inclusions,
        exclusions: typeof form.exclusions === "string" ? form.exclusions.split("\n").filter(Boolean) : form.exclusions,
      };
      if (editing) await api.patch(`/admin/safaris/${editing.id}`, payload);
      else await api.post("/admin/safaris", payload);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Deactivate this safari?")) return;
    try { await api.delete(`/admin/safaris/${id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-serif text-xl text-[#1E3A2B]">Safari products</h2>
        <button onClick={openNew} className="jt-pill-accent text-sm" data-testid="admin-new-safari"><Plus size={14}/>New safari</button>
      </div>
      <div className="bg-white rounded-xl ring-1 ring-black/5 divide-y">
        {list.map((s) => (
          <div key={s.id} className="p-4 flex items-center gap-4" data-testid={`admin-safari-row-${s.slug}`}>
            <img src={s.image_url} alt="" className="w-20 h-14 rounded-md object-cover"/>
            <div className="flex-1">
              <div className="font-medium">{s.name} {!s.is_active && <span className="text-xs text-[#C86D51]">(inactive)</span>}</div>
              <div className="text-xs text-[#5C6B63]">{s.category} · {inr(s.base_price)} · {s.booking_mode === "on_request" ? "on request" : s.shared_pool ? "shared fleet" : "own slot jeeps"}</div>
            </div>
            <button onClick={() => openEdit(s)} className="p-2" data-testid={`edit-${s.slug}`}><Pencil size={16}/></button>
            <button onClick={() => del(s.id)} className="p-2 text-[#C86D51]" data-testid={`del-${s.slug}`}><Trash2 size={16}/></button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit safari" : "New safari"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <TextField label="Name" v={form.name} onC={(v) => setForm({ ...form, name: v })} testid="f-name"/>
            <div>
              <label className="jt-eyebrow mb-2 block">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="f-cat"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["private","jungle","birding","sunrise","sunset","photography","custom"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <TextField label="Base price (INR)" type="number" v={form.base_price} onC={(v) => setForm({ ...form, base_price: v })} testid="f-price"/>
            <div>
              <label className="jt-eyebrow mb-2 block">Pricing</label>
              <div className="h-10 flex items-center text-sm text-[#5C6B63]" data-testid="f-pricing">Per private jeep</div>
            </div>
            <TextField label="Duration (hrs)" type="number" v={form.duration_hours} onC={(v) => setForm({ ...form, duration_hours: v })} testid="f-dur"/>
            <TextField label="Vehicle capacity" type="number" v={form.vehicle_capacity} onC={(v) => setForm({ ...form, vehicle_capacity: v })} testid="f-cap"/>
            <div className="md:col-span-2">
              <TextField label="Image URL" v={form.image_url} onC={(v) => setForm({ ...form, image_url: v })} testid="f-img"/>
            </div>
            <div className="md:col-span-2">
              <label className="jt-eyebrow mb-2 block">Short description</label>
              <Textarea rows={2} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} data-testid="f-short"/>
            </div>
            <div className="md:col-span-2">
              <label className="jt-eyebrow mb-2 block">Full description</label>
              <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="f-desc"/>
            </div>
            <div>
              <label className="jt-eyebrow mb-2 block">Inclusions (one per line)</label>
              <Textarea rows={4} value={Array.isArray(form.inclusions) ? form.inclusions.join("\n") : form.inclusions} onChange={(e) => setForm({ ...form, inclusions: e.target.value })}/>
            </div>
            <div>
              <label className="jt-eyebrow mb-2 block">Exclusions (one per line)</label>
              <Textarea rows={4} value={Array.isArray(form.exclusions) ? form.exclusions.join("\n") : form.exclusions} onChange={(e) => setForm({ ...form, exclusions: e.target.value })}/>
            </div>
            <div className="md:col-span-2">
              <SessionsEditor value={form.sessions || []} onChange={(v) => setForm({ ...form, sessions: v })}/>
            </div>
            <div className="md:col-span-2 flex items-start gap-3 rounded-lg bg-[#F5F0EB] p-3">
              <Switch checked={!!form.shared_pool} onCheckedChange={(v) => setForm({ ...form, shared_pool: v })} data-testid="f-shared-pool"/>
              <span className="text-sm">Use shared jeep fleet<span className="block text-xs text-[#5C6B63]">Jeeps come from your active fleet (Admin → Fleet), shared with every other shared-fleet safari in the same morning or evening session. Slot jeep counts are ignored.</span></span>
            </div>
            <div className="md:col-span-2 flex items-start gap-3 rounded-lg bg-[#F5F0EB] p-3">
              <Switch checked={form.booking_mode === "on_request"} onCheckedChange={(v) => setForm({ ...form, booking_mode: v ? "on_request" : "instant" })} data-testid="f-on-request"/>
              <span className="text-sm">On request only (Experiences)<span className="block text-xs text-[#5C6B63]">Shown under Experiences. Guests send a request instead of booking a slot.</span></span>
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="f-active"/>
              <span className="text-sm">Active (visible on site)</span>
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} className="jt-pill-primary" data-testid="f-save">Save safari</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TextField({ label, v, onC, type = "text", testid }) {
  return (
    <div>
      <label className="jt-eyebrow mb-2 block">{label}</label>
      <Input type={type} value={v} onChange={(e) => onC(e.target.value)} data-testid={testid}/>
    </div>
  );
}

const SLOT_FILTER0 = { range: undefined, time: "all", status: "all", avail: "all", hidePast: true };
const AVAIL_OPTS = [["all", "Any availability"], ["available", "Has jeeps left"], ["booked", "Has bookings"], ["soldout", "Sold out"], ["empty", "No bookings"], ["started", "Started / past"]];

function applySlotFilter(slots, f) {
  const today = ymd(new Date());
  const from = f.range?.from ? ymd(f.range.from) : null;
  const to = f.range?.from ? ymd(f.range.to || f.range.from) : null;
  return slots.filter((s) => {
    if (f.hidePast && f.avail !== "started" && s.date < today) return false;
    if (from && (s.date < from || s.date > to)) return false;
    if (f.time !== "all" && s.departure_time !== f.time) return false;
    if (f.status !== "all" && s.is_active !== (f.status === "active")) return false;
    const booked = s.booked_vehicles > 0 || s.booked_seats > 0;
    if (f.avail === "available" && !(s.remaining_vehicles > 0)) return false;
    if (f.avail === "booked" && !booked) return false;
    if (f.avail === "soldout" && (s.started || s.remaining_vehicles > 0)) return false;
    if (f.avail === "empty" && booked) return false;
    if (f.avail === "started" && !s.started) return false;
    return true;
  });
}

function SlotFilters({ f, setF, times, shown, total }) {
  const pill = (active) => `h-9 text-sm ${active ? "border-[#1E3A2B] ring-1 ring-[#1E3A2B]" : ""}`;
  return (
    <div className="bg-white rounded-xl ring-1 ring-black/5 p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="slot-filters">
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className={`px-3 rounded-md border border-[#E2DACF] flex items-center gap-2 ${pill(!!f.range?.from)}`} data-testid="slot-filter-dates">
            <CalendarIcon size={14}/>{f.range?.from ? `${fmtDay(ymd(f.range.from))} → ${fmtDay(ymd(f.range.to || f.range.from))}` : "Any date"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-auto" align="start"><Calendar mode="range" numberOfMonths={2} selected={f.range} onSelect={(r) => setF({ ...f, range: r })}/></PopoverContent>
      </Popover>
      <Select value={f.time} onValueChange={(v) => setF({ ...f, time: v })}>
        <SelectTrigger className={`w-44 ${pill(f.time !== "all")}`} data-testid="slot-filter-session"><SelectValue/></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All sessions</SelectItem>
          {times.map(([t, l]) => <SelectItem key={t} value={t}>{l} · {t}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={f.avail} onValueChange={(v) => setF({ ...f, avail: v })}>
        <SelectTrigger className={`w-44 ${pill(f.avail !== "all")}`} data-testid="slot-filter-availability"><SelectValue/></SelectTrigger>
        <SelectContent>{AVAIL_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
        <SelectTrigger className={`w-36 ${pill(f.status !== "all")}`} data-testid="slot-filter-status"><SelectValue/></SelectTrigger>
        <SelectContent><SelectItem value="all">Active + off</SelectItem><SelectItem value="active">Active only</SelectItem><SelectItem value="inactive">Switched off</SelectItem></SelectContent>
      </Select>
      <label className="flex items-center gap-2 text-sm px-2"><Switch checked={f.hidePast} onCheckedChange={(v) => setF({ ...f, hidePast: v })} data-testid="slot-filter-hide-past"/>Hide past dates</label>
      <button type="button" onClick={() => setF(SLOT_FILTER0)} className="text-sm underline text-[#5C6B63]" data-testid="slot-filter-reset">Reset</button>
      <span className="ml-auto text-sm text-[#5C6B63]" data-testid="slot-filter-count">Showing {shown} of {total}</span>
    </div>
  );
}

function DeleteSlotsDialog({ open, onOpenChange, safari, onDone }) {
  const [scope, setScope] = useState("safari");
  const [range, setRange] = useState(undefined);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setScope("safari"); setRange(undefined); } }, [open]);
  const what = `${scope === "all" ? "ALL safaris" : safari?.name}, ${range?.from ? `${fmtDay(ymd(range.from))} → ${fmtDay(ymd(range.to || range.from))}` : "all dates"}`;
  const go = async () => {
    if (!window.confirm(`Delete slots for ${what}? Slots with active bookings are kept.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/slots/bulk-delete", {
        safari_id: scope === "all" ? null : safari?.id,
        date_from: range?.from ? ymd(range.from) : null, date_to: range?.from ? ymd(range.to || range.from) : null,
      });
      toast.success(`${data.deleted} slot${data.deleted === 1 ? "" : "s"} deleted${data.kept_with_bookings ? ` · ${data.kept_with_bookings} kept (have bookings)` : ""}`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="delete-slots-dialog">
        <DialogHeader><DialogTitle>Delete slots</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          {[["safari", `Only ${safari?.name || "this safari"}`], ["all", "All safaris"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setScope(v)} data-testid={`delete-scope-${v}`}
              className={`px-3 py-1.5 rounded-full text-sm border ${scope === v ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "border-[#E2DACF]"}`}>{l}</button>
          ))}
        </div>
        <div>
          <label className="jt-eyebrow mb-2 block">Dates (leave empty for all dates)</label>
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="h-10 w-full rounded-md border border-[#E2DACF] px-3 flex items-center gap-2 text-sm" data-testid="delete-dates-trigger">
                <CalendarIcon size={15}/>{range?.from ? `${fmtDay(ymd(range.from))} → ${fmtDay(ymd(range.to || range.from))}` : "All dates"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-auto" align="start"><Calendar mode="range" numberOfMonths={2} selected={range} onSelect={setRange}/></PopoverContent>
          </Popover>
          {range?.from && <button type="button" onClick={() => setRange(undefined)} className="text-xs underline mt-1">Clear dates</button>}
        </div>
        <p className="text-xs text-[#5C6B63]">Slots that have confirmed bookings (or a guest mid-checkout) are kept so no guest is stranded. Cancel those bookings first if you need them gone.</p>
        <DialogFooter><button onClick={go} disabled={busy} className="jt-pill text-sm bg-[#A33A2A] text-white hover:bg-[#8A2F22] disabled:opacity-40" data-testid="delete-slots-confirm">Delete slots</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SlotManager() {
  const [safaris, setSafaris] = useState([]);
  const [safariId, setSafariId] = useState("");
  const [slots, setSlots] = useState([]);
  const [open, setOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [filt, setFilt] = useState(SLOT_FILTER0);
  const shownSlots = applySlotFilter(slots, filt);
  const times = Array.from(new Map(slots.map((s) => [s.departure_time, s.label || s.departure_time])).entries()).sort();
  const [form, setForm] = useState({ safari_id: "", date_from: "", date_to: "", departure_time: "06:30", label: "Morning", total_vehicles: 3, seats_per_vehicle: 6, price_override: "" });

  useEffect(() => { api.get("/admin/safaris").then((r) => { setSafaris(r.data); setSafariId(r.data[0]?.id || ""); }); }, []);
  const load = () => safariId && api.get("/admin/slots", { params: { safari_id: safariId } }).then((r) => setSlots(r.data));
  useEffect(() => { load(); }, [safariId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      const payload = { ...form, safari_id: form.safari_id || safariId,
        total_vehicles: Number(form.total_vehicles),
        seats_per_vehicle: Number(form.seats_per_vehicle),
        price_override: form.price_override === "" || form.price_override == null ? null : Number(form.price_override),
      };
      const { data } = await api.post("/admin/slots/bulk", { ...payload, date_to: form.date_to || form.date_from });
      toast.success(`${data.created} slot${data.created === 1 ? "" : "s"} added${data.skipped ? ` · ${data.skipped} skipped (already existed)` : ""}`);
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggle = async (slot) => {
    try {
      const { safari_id, date, departure_time, label, total_vehicles, seats_per_vehicle, price_override } = slot;
      await api.patch(`/admin/slots/${slot.id}`, { safari_id, date, departure_time, label, total_vehicles, seats_per_vehicle, price_override, is_active: !slot.is_active });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Select value={safariId} onValueChange={setSafariId}>
          <SelectTrigger className="w-72" data-testid="slot-safari-select"><SelectValue/></SelectTrigger>
          <SelectContent>
            {safaris.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={() => setDelOpen(true)} className="jt-pill text-sm ml-auto border-2 border-[#A33A2A] text-[#A33A2A] hover:bg-[#A33A2A] hover:text-white" data-testid="admin-delete-slots"><Trash2 size={14}/>Delete slots</button>
        <button onClick={() => { const ses = safaris.find((x) => x.id === safariId)?.sessions?.[0]; setForm({ ...form, safari_id: safariId, departure_time: ses?.time || "06:30", label: ses?.label || "" }); setOpen(true); }} className="jt-pill-accent text-sm" data-testid="admin-new-slot"><Plus size={14}/>Add slot</button>
      </div>
      <SlotFilters f={filt} setF={setFilt} times={times} shown={shownSlots.length} total={slots.length}/>
      <div className="bg-white rounded-xl ring-1 ring-black/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0EB] text-left"><tr>
            <th className="p-3">Date</th><th>Time</th><th>Label</th><th>Jeeps</th><th>Jeeps booked / left</th><th>Seats/jeep</th><th>Price</th><th>Active</th>
          </tr></thead>
          <tbody>
            {shownSlots.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-[#5C6B63]" data-testid="slot-filter-empty">No slots match these filters.</td></tr>}
            {shownSlots.map((s) => (
              <tr key={s.id} className="border-t" data-testid={`slot-row-${s.id}`}>
                <td className="p-3">{s.date}</td>
                <td>{s.departure_time}{s.started && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#F5F0EB] text-[#5C6B63]" data-testid={`slot-started-${s.id}`}>started</span>}</td>
                <td>{s.label}</td>
                <td data-testid={`slot-total-${s.id}`}>{s.total_vehicles}{s.shared_pool && <div className="text-[10px] text-[#5C6B63]">shared fleet · {s.session}</div>}</td>
                <td data-testid={`slot-jeeps-${s.id}`}><span className="font-medium">{s.booked_vehicles}</span> / <span className={s.remaining_vehicles === 0 ? "text-[#C86D51] font-semibold" : "text-[#1E3A2B] font-semibold"}>{s.remaining_vehicles}</span></td>
                <td>{s.seats_per_vehicle}</td>
                <td>{s.price_override ? inr(s.price_override) : "—"}</td>
                <td><Switch checked={s.is_active} onCheckedChange={() => toggle(s)} data-testid={`slot-toggle-${s.id}`}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DeleteSlotsDialog open={delOpen} onOpenChange={setDelOpen} safari={safaris.find((x) => x.id === safariId)} onDone={load}/>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add jeep slots</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5C6B63] -mt-2">Creates one slot per day between the two dates. Days that already have this time are skipped.</p>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="jt-eyebrow mb-2 block">Dates (click start, then end)</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="h-10 w-full rounded-md border border-[#E2DACF] bg-white px-3 flex items-center gap-2 text-sm" data-testid="slot-dates-trigger">
                    <CalendarIcon size={15} className="text-[#5C6B63]"/>
                    {form.date_from ? `${fmtDay(form.date_from)} → ${fmtDay(form.date_to || form.date_from)}` : "Pick from and to dates"}
                    {form.date_from && <span className="ml-auto text-xs text-[#5C6B63]" data-testid="slot-days-count">{dayCount(form.date_from, form.date_to)} days</span>}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-auto" align="start">
                  <Calendar mode="range" numberOfMonths={2} data-testid="slot-range-calendar"
                    selected={{ from: toDate(form.date_from), to: toDate(form.date_to) }}
                    onSelect={(r) => setForm({ ...form, date_from: r?.from ? ymd(r.from) : "", date_to: r?.to ? ymd(r.to) : (r?.from ? ymd(r.from) : "") })}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}/>
                </PopoverContent>
              </Popover>
            </div>
            <div className="md:col-span-2">
              <label className="jt-eyebrow mb-2 block">Session</label>
              <div className="flex flex-wrap gap-2" data-testid="slot-session-options">
                {(safaris.find((x) => x.id === safariId)?.sessions || []).map((ses) => (
                  <button key={ses.time} type="button" onClick={() => setForm({ ...form, departure_time: ses.time, label: ses.label })} data-testid={`slot-session-${ses.time}`}
                    className={`px-4 py-2 rounded-full text-sm border ${form.departure_time === ses.time ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "border-[#E2DACF] bg-white"}`}>
                    {ses.label} · {ses.time}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#5C6B63] mt-2">Sessions are set on the safari (Safaris tab → edit). Guests only see these departures.</p>
            </div>
            <TextField label="Vehicles" type="number" v={form.total_vehicles} onC={(v) => setForm({ ...form, total_vehicles: v })} testid="slot-veh"/>
            <TextField label="Seats/jeep" type="number" v={form.seats_per_vehicle} onC={(v) => setForm({ ...form, seats_per_vehicle: v })} testid="slot-spv"/>
            <TextField label="Price override (optional)" type="number" v={form.price_override} onC={(v) => setForm({ ...form, price_override: v })} testid="slot-price"/>
          </div>
          <DialogFooter><button onClick={save} disabled={!form.date_from} className="jt-pill-primary disabled:opacity-40" data-testid="slot-save">Add slots</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookingList() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("");
  const [sel, setSel] = useState(null);
  const [dlg, setDlg] = useState("");
  const load = () => api.get("/admin/bookings", { params: filter ? { status: filter } : {} }).then((r) => setBookings(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = (b, d) => { setSel(b); setDlg(d); };
  const markPaid = async (b) => {
    if (!window.confirm(`Mark ${b.booking_ref} as paid (${inr(b.amount)})? The guest will get the confirmation email and voucher.`)) return;
    try { await api.post(`/admin/bookings/${b.id}/mark-paid`); toast.success("Booking confirmed and voucher sent"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const remind = async (b) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/payment-reminder`);
      if (w) w.location.href = data.whatsapp_url; else window.location.href = data.whatsapp_url;
      toast.success("WhatsApp reminder opened");
      load();
    } catch (e) { if (w) w.close(); toast.error(formatErr(e)); }
  };
  const markRefunded = async (b) => {
    try { await api.post(`/admin/bookings/${b.id}/refunds/processed`); toast.success("Refund marked as processed"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="mt-6">
      <div className="flex gap-2 mb-4">
        {["", "confirmed", "pending", "cancelled"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`bk-filter-${f || "all"}`}
            className={`px-3 py-1.5 rounded-full text-sm border ${filter === f ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "bg-white border-[#E2DACF]"}`}>
            {f || "all"}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-xl ring-1 ring-black/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0EB] text-left"><tr>
            <th className="p-3">Ref</th><th>Booking</th><th>Date</th><th>Type</th><th>Guest</th><th>Amount</th><th>Status</th><th>Refund</th><th>Email</th><th className="pr-3">Actions</th>
          </tr></thead>
          <tbody>
            {bookings.map((b) => {
              const pendingRefund = (b.refunds || []).filter((r) => r.status === "pending").reduce((t, r) => t + r.amount, 0);
              const refunded = (b.refunds || []).filter((r) => r.status === "processed").reduce((t, r) => t + r.amount, 0);
              return (
                <tr key={b.id} className="border-t align-top" data-testid={`bk-row-${b.booking_ref}`}>
                  <td className="p-3 font-mono text-[#1E3A2B]">{b.booking_ref}{b.history?.length > 0 && <div className="text-[10px] text-[#5C6B63] font-sans">{b.history.length} change{b.history.length > 1 ? "s" : ""}</div>}</td>
                  <td className="py-3">{b.title || b.safari_name}{b.room_name && <div className="text-xs text-[#5C6B63]">{b.room_name}{b.rooms > 1 ? ` × ${b.rooms}` : ""}</div>}</td>
                  <td className="py-3">{b.check_in ? `${b.check_in} → ${b.check_out}` : `${b.slot_date} ${b.slot_time}`}{b.kind === "package" && <div className="text-xs text-[#5C6B63]">Safari {b.slot_date} {b.slot_time}</div>}</td>
                  <td className="py-3">{b.kind || "safari"}{b.booking_type && b.kind !== "stay" ? ` · ${b.booking_type}` : ""}{b.vehicles > 0 && <div className="text-xs text-[#5C6B63]">{b.vehicles} jeep{b.vehicles > 1 ? "s" : ""}</div>}</td>
                  <td className="py-3">{b.guest_name}<div className="text-xs text-[#5C6B63]">{b.guest_mobile}</div></td>
                  <td className="py-3">{inr(b.amount)}</td>
                  <td className="py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${b.status === "confirmed" ? "bg-[#1E3A2B] text-white" : b.status === "cancelled" ? "bg-[#F6E3DE] text-[#A33A2A]" : "bg-[#F5F0EB]"}`}>{b.status}</span>{b.cancelled_by && <div className="text-[10px] text-[#5C6B63] mt-1">by {b.cancelled_by}</div>}</td>
                  <td className="py-3 text-xs" data-testid={`bk-refund-${b.booking_ref}`}>
                    {pendingRefund > 0 && <div className="text-[#C86D51] font-semibold">{inr(pendingRefund)} due</div>}
                    {refunded > 0 && <div className="text-[#1E3A2B]">{inr(refunded)} refunded</div>}
                    {!pendingRefund && !refunded && "—"}
                  </td>
                  <td className={`py-3 text-xs ${b.email_status === "failed" ? "text-[#C86D51] font-semibold" : "text-[#5C6B63]"}`} data-testid={`bk-email-${b.booking_ref}`}>{b.email_status || "—"}</td>
                  <td className="py-3 pr-3">
                    <div className="flex flex-col gap-1 items-start">
                      {b.status === "pending" && <button onClick={() => markPaid(b)} className="text-xs text-[#1E3A2B] font-semibold underline" data-testid={`bk-markpaid-${b.booking_ref}`}>Mark paid</button>}
                      {b.status === "pending" && <button onClick={() => remind(b)} className="text-xs text-[#25D366] font-semibold inline-flex items-center gap-1 hover:underline" data-testid={`bk-remind-${b.booking_ref}`}><MessageCircle size={12}/> WhatsApp reminder</button>}
                      {b.status === "pending" && b.payment_reminded_at && <span className="text-[10px] text-[#5C6B63]" data-testid={`bk-reminded-${b.booking_ref}`}>Reminded{b.payment_reminders > 1 ? ` ×${b.payment_reminders}` : ""} · {new Date(b.payment_reminded_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                      {b.status === "confirmed" && <button onClick={() => open(b, "modify")} className="text-xs text-[#1E3A2B] underline" data-testid={`bk-modify-${b.booking_ref}`}>Modify</button>}
                      {["confirmed", "pending"].includes(b.status) && <button onClick={() => open(b, "cancel")} className="text-xs text-[#A33A2A] underline" data-testid={`bk-cancel-${b.booking_ref}`}>Cancel</button>}
                      {pendingRefund > 0 && <button onClick={() => markRefunded(b)} className="text-xs text-[#C86D51] underline" data-testid={`bk-refunded-${b.booking_ref}`}>Mark refunded</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {bookings.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-[#5C6B63]">No bookings yet</td></tr>}
          </tbody>
        </table>
      </div>
      {sel && (
        <>
          <ModifyDialog booking={sel} open={dlg === "modify"} onOpenChange={(o) => !o && setDlg("")}
            note="Admin change: any date or price. Extra amounts are recorded as collected (payments are MOCKED); lower prices create a pending refund."
            quoteFn={(ch) => api.post(`/admin/bookings/${sel.id}/modify-quote`, ch)}
            submitFn={(ch) => api.post(`/admin/bookings/${sel.id}/modify`, ch)}
            onDone={load}/>
          <CancelDialog booking={sel} admin open={dlg === "cancel"} onOpenChange={(o) => !o && setDlg("")}
            previewFn={() => api.get(`/admin/bookings/${sel.id}/cancel-preview`)}
            submitFn={(body) => api.post(`/admin/bookings/${sel.id}/cancel`, body)}
            onDone={load}/>
        </>
      )}
    </div>
  );
}
